import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { KeyRound, Zap, Loader2, CheckCircle2, XCircle, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
  user: 'A user-provided key',
  admin: 'Your platform key (admin panel)',
  env: 'Server environment key (CUSTOM_LLM_API_KEY)',
  emergent: 'Emergent Universal Key (billed to your Emergent balance)',
};

const USAGE_LABELS = {
  user: 'User keys',
  admin: 'Your platform key',
  env: 'Environment key',
  emergent: 'Emergent Universal Key',
  emergent_fallback: 'Emergent (auto-fallback)',
};

function UsageMeter() {
  const [usage, setUsage] = useState(null);
  useEffect(() => {
    axios.get(`${API}/admin/llm-usage`, { withCredentials: true }).then(r => setUsage(r.data)).catch(() => {});
  }, []);
  if (!usage) return null;
  return (
    <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5" data-testid="aikeys-usage-meter">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-bold text-sm flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#06B6D4]" /> AI Usage Meter</h3>
        <span className="text-xs text-zinc-500 font-mono">today: <span className="text-white font-bold" data-testid="aikeys-usage-today-total">{usage.today_total}</span> calls · 7d: {usage.week_total}</span>
      </div>
      {usage.today.length === 0 ? (
        <p className="text-xs text-zinc-500">No AI calls yet today.</p>
      ) : (
        <div className="space-y-1.5 mb-3">
          {usage.today.map((r, i) => (
            <div key={i} className="flex items-center justify-between bg-[#121214] border border-[#1E1E24] rounded-md px-3 py-1.5 text-xs" data-testid={`aikeys-usage-row-${r.source}`}>
              <span className="text-zinc-300">{USAGE_LABELS[r.source] || r.source} <span className="text-zinc-600 font-mono">({r.provider} / {r.model})</span></span>
              <span className="text-white font-mono font-bold">{r.calls} calls</span>
            </div>
          ))}
        </div>
      )}
      {usage.week.length > 0 && (
        <details className="text-xs text-zinc-500">
          <summary className="cursor-pointer hover:text-zinc-300">Last 7 days breakdown</summary>
          <div className="mt-2 space-y-1">
            {usage.week.map((r, i) => (
              <div key={i} className="flex justify-between font-mono">
                <span>{r.date} — {USAGE_LABELS[r.source] || r.source}</span><span>{r.calls}</span>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

export const AdminAIKeysTab = () => {
  const [settings, setSettings] = useState(null);
  const [oaiKey, setOaiKey] = useState('');
  const [antKey, setAntKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/llm-settings`, { withCredentials: true });
      setSettings(r.data);
    } catch (e) { toast.error('Failed to load AI key settings'); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/admin/llm-settings`, payload, { withCredentials: true });
      setSettings(r.data);
      setOaiKey(''); setAntKey('');
      toast.success('AI key settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await axios.post(`${API}/admin/llm-settings/test`, {}, { withCredentials: true });
      setTestResult(r.data);
      r.data.ok ? toast.success(`AI responding via ${r.data.provider} (${SOURCE_LABELS[r.data.source] || r.data.source})`)
                : toast.error(`Test failed: ${r.data.error}`);
    } catch (e) { toast.error('Test request failed'); }
    finally { setTesting(false); }
  };

  if (!settings) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#06B6D4] animate-spin" /></div>;

  return (
    <div className="grid gap-4 max-w-6xl lg:grid-cols-2 items-start" data-testid="admin-aikeys-tab">
      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5">
        <h3 className="text-white font-bold flex items-center gap-2 mb-1"><KeyRound className="w-4 h-4 text-[#06B6D4]" /> AI Billing Route</h3>
        <p className="text-xs text-zinc-500 mb-3">All engine AI (Copilot, behavior scripts, game generation, chat designer) is billed to the first key found: user key → your platform key → Emergent Universal Key. If a custom key fails mid-call, the request automatically retries on the Emergent key — AI never goes down because one key went bad.</p>
        <div className="flex items-center gap-2 bg-[#121214] border border-[#1E1E24] rounded-lg px-3 py-2" data-testid="aikeys-active-source">
          <Zap className="w-4 h-4 text-amber-400 shrink-0" />
          <span className="text-sm text-zinc-300">Currently billing: <span className="text-white font-semibold">{SOURCE_LABELS[settings.active_source] || settings.active_source}</span> <span className="text-zinc-500 font-mono text-xs">({settings.active_provider} / {settings.active_model})</span></span>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Button size="sm" onClick={runTest} disabled={testing} className="bg-[#06B6D4] hover:bg-[#0891B2] text-black text-xs" data-testid="aikeys-test-btn">
            {testing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Testing…</> : 'Test AI Connection'}
          </Button>
          {testResult && (
            <span className={`text-xs flex items-center gap-1 ${testResult.ok ? 'text-emerald-400' : 'text-red-400'}`} data-testid="aikeys-test-result">
              {testResult.ok ? <><CheckCircle2 className="w-3.5 h-3.5" /> Working via {testResult.provider}</> : <><XCircle className="w-3.5 h-3.5" /> {String(testResult.error).slice(0, 120)}</>}
            </span>
          )}
        </div>
      </div>

      <UsageMeter />

      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5 space-y-4">
        <h3 className="text-white font-bold text-sm">Platform Keys (yours — override Emergent billing)</h3>
        {[
          { label: 'OpenAI API Key', masked: settings.openai_key_masked, val: oaiKey, set: setOaiKey, field: 'openai_key', ph: 'sk-…', testid: 'aikeys-openai-input' },
          { label: 'Anthropic (Claude) API Key', masked: settings.anthropic_key_masked, val: antKey, set: setAntKey, field: 'anthropic_key', ph: 'sk-ant-…', testid: 'aikeys-anthropic-input' },
        ].map(k => (
          <div key={k.field}>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{k.label} {k.masked && <span className="text-emerald-400 normal-case font-mono ml-1">saved: {k.masked}</span>}</label>
            <div className="flex gap-2">
              <Input type="password" placeholder={k.masked ? 'Enter a new key to replace' : k.ph} value={k.val}
                onChange={(e) => k.set(e.target.value)} data-testid={k.testid}
                className="bg-[#121214] border-[#1E1E24] text-white text-sm font-mono" />
              <Button size="sm" disabled={saving || !k.val.trim()} onClick={() => save({ [k.field]: k.val.trim() })}
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid={`${k.testid}-save`}>Save</Button>
              {k.masked && (
                <Button size="sm" variant="ghost" disabled={saving} onClick={() => save({ [k.field]: '' })}
                  className="text-zinc-500 hover:text-red-400 text-xs" data-testid={`${k.testid}-clear`}>Clear</Button>
              )}
            </div>
          </div>
        ))}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Preferred provider (when both keys are saved)</label>
          <select value={settings.preferred_provider}
            onChange={(e) => save({ preferred_provider: e.target.value })}
            className="bg-[#121214] border border-[#1E1E24] rounded-md px-3 h-9 text-sm text-white"
            data-testid="aikeys-preferred-provider">
            <option value="openai">OpenAI</option>
            <option value="anthropic">Anthropic (Claude)</option>
          </select>
        </div>
      </div>

      <div className="bg-[#0A0A0C] border border-[#1E1E24] rounded-xl p-5 flex items-center justify-between gap-4">
        <div>
          <h3 className="text-white font-bold text-sm">Allow users to bring their own keys</h3>
          <p className="text-xs text-zinc-500 mt-1">When on, users can save personal OpenAI/Anthropic keys in Settings — their AI usage bills to them. When off, everyone uses the platform route above.</p>
        </div>
        <Switch checked={settings.allow_user_keys} onCheckedChange={(v) => save({ allow_user_keys: v })} data-testid="aikeys-allow-user-toggle" />
      </div>
    </div>
  );
};

export default AdminAIKeysTab;
